import React from 'react'
import { DesktopUserActions, UserActionsDropdown } from './AdminUserRowActions'
import { formatUserName, isEmailVerified } from './adminUsersUtils'

function SortableColumnHeader({
  column,
  label,
  currentSort,
  currentDirection,
  onSort,
}) {
  const isActive = currentSort === column
  const iconClass = isActive
    ? currentDirection === 'asc'
      ? 'bi bi-caret-up-fill'
      : 'bi bi-caret-down-fill'
    : 'bi bi-arrow-down-up'

  return (
    <th
      aria-sort={
        isActive
          ? currentDirection === 'asc'
            ? 'ascending'
            : 'descending'
          : 'none'
      }
    >
      <button
        type="button"
        className="admin-users-sortable-header"
        onClick={() => onSort(column)}
        aria-label={`Sort by ${label}`}
      >
        {label}
        <span className="admin-users-sort-indicator" aria-hidden="true">
          <i className={iconClass}></i>
        </span>
      </button>
    </th>
  )
}

function MobileUserList({
  users,
  user,
  impersonating,
  onMakeAdmin,
  onImpersonate,
  onDelete,
  onResetPassword,
  onEdit,
  editingUserId,
  editFormData,
  setEditFormData,
  editErrors,
  onSave,
  onCancel,
  isSaving,
}) {
  return (
    <div className="d-md-none">
      {users.map((userRow) => {
        const isEditing = editingUserId === userRow.id
        return (
          <div
            key={userRow.id}
            id={`user-row-${userRow.id}`}
            className="card mb-3 admin-users-mobile-card"
          >
            <div className="card-body">
              {isEditing ? (
                <div className="d-flex flex-column gap-2">
                  <div className="mb-2">
                    <label className="form-label small text-muted mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      className={`form-control form-control-sm ${editErrors?.email ? 'is-invalid' : ''}`}
                      value={editFormData.email}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          email: e.target.value,
                        })
                      }
                      placeholder="Email"
                    />
                    {editErrors?.email && (
                      <div className="invalid-feedback d-block">
                        {editErrors.email}
                      </div>
                    )}
                  </div>

                  <div className="mb-2">
                    <label className="form-label small text-muted mb-1">
                      Name
                    </label>
                    <div className="d-flex gap-2">
                      <div className="flex-grow-1">
                        <input
                          type="text"
                          className={`form-control form-control-sm ${editErrors?.firstname ? 'is-invalid' : ''}`}
                          value={editFormData.firstname}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              firstname: e.target.value,
                            })
                          }
                          placeholder="First"
                        />
                      </div>
                      <div className="flex-grow-1">
                        <input
                          type="text"
                          className={`form-control form-control-sm ${editErrors?.lastname ? 'is-invalid' : ''}`}
                          value={editFormData.lastname}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              lastname: e.target.value,
                            })
                          }
                          placeholder="Last"
                        />
                      </div>
                    </div>
                    {(editErrors?.firstname || editErrors?.lastname) && (
                      <div className="invalid-feedback d-block">
                        {editErrors.firstname || editErrors.lastname}
                      </div>
                    )}
                  </div>

                  <div className="mb-2">
                    <label className="form-label small text-muted mb-1">
                      Info
                    </label>
                    <div className="d-flex gap-2 align-items-center">
                      <select
                        className={`form-select form-select-sm ${editErrors?.user_type ? 'is-invalid' : ''}`}
                        value={editFormData.user_type}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            user_type: e.target.value,
                          })
                        }
                        disabled={
                          userRow.user_type === 'owner' &&
                          userRow.id === user?.id
                        }
                        style={{ width: 'auto' }}
                      >
                        <option value="player">player</option>
                        <option value="admin">admin</option>
                        {userRow.user_type === 'owner' && (
                          <option value="owner">owner</option>
                        )}
                      </select>

                      <div className="form-check ms-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={editFormData.email_verified}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              email_verified: e.target.checked,
                            })
                          }
                          id={`verified-mobile-${userRow.id}`}
                        />
                        <label
                          className="form-check-label small"
                          htmlFor={`verified-mobile-${userRow.id}`}
                        >
                          Verified
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex gap-2 mt-2">
                    <button
                      className="btn btn-primary btn-sm flex-grow-1"
                      onClick={onSave}
                      disabled={isSaving}
                    >
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={onCancel}
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                  </div>
                  {editErrors?.general && (
                    <div className="text-danger small mt-1 text-center">
                      {editErrors.general}
                    </div>
                  )}
                </div>
              ) : (
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <h6 className="card-title mb-1">{userRow.email}</h6>
                    <div className="small">{formatUserName(userRow)}</div>
                    <small className="text-muted">
                      {userRow.user_type} •{' '}
                      {isEmailVerified(userRow) ? 'Verified' : 'Unverified'}
                      {userRow.created_at &&
                        ` • ${new Date(userRow.created_at).toLocaleDateString()}`}
                    </small>
                    <div className="small text-muted mt-1">
                      <span className="badge bg-light text-dark me-2">
                        {userRow.registration_count || 0} Registration
                        {userRow.registration_count === 1 ? '' : 's'}
                      </span>
                      <span className="badge bg-light text-dark">
                        {userRow.active_league_count || 0} Active League
                        {userRow.active_league_count === 1 ? '' : 's'}
                      </span>
                    </div>
                  </div>
                  <UserActionsDropdown
                    userRow={userRow}
                    user={user}
                    impersonating={impersonating}
                    onMakeAdmin={onMakeAdmin}
                    onImpersonate={onImpersonate}
                    onDelete={onDelete}
                    onResetPassword={onResetPassword}
                    onEdit={onEdit}
                  />
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function AdminUsersTable(props) {
  const {
    users,
    user,
    impersonating,
    sortColumn,
    sortDirection,
    onSort,
    onMakeAdmin,
    onImpersonate,
    onDelete,
    onResetPassword,
    onEdit,
    editingUserId,
    editFormData,
    setEditFormData,
    editErrors,
    onSave,
    onCancel,
    isSaving,
  } = props

  return (
    <>
      <MobileUserList
        users={users}
        user={user}
        impersonating={impersonating}
        onMakeAdmin={onMakeAdmin}
        onImpersonate={onImpersonate}
        onDelete={onDelete}
        onResetPassword={onResetPassword}
        onEdit={onEdit}
        editingUserId={editingUserId}
        editFormData={editFormData}
        setEditFormData={setEditFormData}
        editErrors={editErrors}
        onSave={onSave}
        onCancel={onCancel}
        isSaving={isSaving}
      />

      <div className="table-responsive d-none d-md-block">
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <SortableColumnHeader
                column="id"
                label="ID"
                currentSort={sortColumn}
                currentDirection={sortDirection}
                onSort={onSort}
              />
              <SortableColumnHeader
                column="email"
                label="Email"
                currentSort={sortColumn}
                currentDirection={sortDirection}
                onSort={onSort}
              />
              <SortableColumnHeader
                column="name"
                label="Name"
                currentSort={sortColumn}
                currentDirection={sortDirection}
                onSort={onSort}
              />
              <SortableColumnHeader
                column="user_type"
                label="User Type"
                currentSort={sortColumn}
                currentDirection={sortDirection}
                onSort={onSort}
              />
              <SortableColumnHeader
                column="verified"
                label="Verified"
                currentSort={sortColumn}
                currentDirection={sortDirection}
                onSort={onSort}
              />
              <SortableColumnHeader
                column="registration_count"
                label="Registrations"
                currentSort={sortColumn}
                currentDirection={sortDirection}
                onSort={onSort}
              />
              <SortableColumnHeader
                column="active_league_count"
                label="Active Leagues"
                currentSort={sortColumn}
                currentDirection={sortDirection}
                onSort={onSort}
              />
              <SortableColumnHeader
                column="created_at"
                label="Created"
                currentSort={sortColumn}
                currentDirection={sortDirection}
                onSort={onSort}
              />
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((userRow) => {
              const isEditing = editingUserId === userRow.id
              return (
                <tr key={userRow.id} id={`user-row-${userRow.id}`}>
                  {isEditing ? (
                    <>
                      <td>{userRow.id}</td>
                      <td>
                        <input
                          type="email"
                          className={`form-control form-control-sm ${editErrors.email ? 'is-invalid' : ''}`}
                          value={editFormData.email}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              email: e.target.value,
                            })
                          }
                          placeholder="Email"
                        />
                        {editErrors.email && (
                          <div className="invalid-feedback d-block">
                            {editErrors.email}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <input
                            type="text"
                            className={`form-control form-control-sm ${editErrors.firstname ? 'is-invalid' : ''}`}
                            value={editFormData.firstname}
                            onChange={(e) =>
                              setEditFormData({
                                ...editFormData,
                                firstname: e.target.value,
                              })
                            }
                            placeholder="First"
                          />
                          <input
                            type="text"
                            className={`form-control form-control-sm ${editErrors.lastname ? 'is-invalid' : ''}`}
                            value={editFormData.lastname}
                            onChange={(e) =>
                              setEditFormData({
                                ...editFormData,
                                lastname: e.target.value,
                              })
                            }
                            placeholder="Last"
                          />
                        </div>
                        {(editErrors.firstname || editErrors.lastname) && (
                          <div className="invalid-feedback d-block">
                            {editErrors.firstname || editErrors.lastname}
                          </div>
                        )}
                      </td>
                      <td>
                        <select
                          className={`form-select form-select-sm ${editErrors.user_type ? 'is-invalid' : ''}`}
                          value={editFormData.user_type}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              user_type: e.target.value,
                            })
                          }
                          disabled={
                            userRow.user_type === 'owner' &&
                            userRow.id === user?.id
                          }
                        >
                          <option value="player">player</option>
                          <option value="admin">admin</option>
                          {userRow.user_type === 'owner' && (
                            <option value="owner">owner</option>
                          )}
                        </select>
                      </td>
                      <td>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={editFormData.email_verified}
                            onChange={(e) =>
                              setEditFormData({
                                ...editFormData,
                                email_verified: e.target.checked,
                              })
                            }
                            id={`verified-desktop-${userRow.id}`}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`verified-desktop-${userRow.id}`}
                          >
                            {editFormData.email_verified ? 'Yes' : 'No'}
                          </label>
                        </div>
                      </td>
                      <td>{userRow.registration_count || 0}</td>
                      <td>{userRow.active_league_count || 0}</td>
                      <td>
                        {userRow.created_at
                          ? new Date(userRow.created_at).toLocaleDateString()
                          : 'N/A'}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={onSave}
                            disabled={isSaving}
                          >
                            {isSaving ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={onCancel}
                            disabled={isSaving}
                          >
                            Cancel
                          </button>
                        </div>
                        {editErrors.general && (
                          <div className="text-danger small mt-1">
                            {editErrors.general}
                          </div>
                        )}
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{userRow.id}</td>
                      <td>{userRow.email}</td>
                      <td>{formatUserName(userRow)}</td>
                      <td>{userRow.user_type}</td>
                      <td>{isEmailVerified(userRow) ? 'Yes' : 'No'}</td>
                      <td>{userRow.registration_count || 0}</td>
                      <td>{userRow.active_league_count || 0}</td>
                      <td>
                        {userRow.created_at
                          ? new Date(userRow.created_at).toLocaleDateString()
                          : 'N/A'}
                      </td>
                      <td>
                        <DesktopUserActions
                          userRow={userRow}
                          user={user}
                          impersonating={impersonating}
                          onMakeAdmin={onMakeAdmin}
                          onImpersonate={onImpersonate}
                          onDelete={onDelete}
                          onResetPassword={onResetPassword}
                          onEdit={onEdit}
                        />
                      </td>
                    </>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}
